import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
    Stack, 
    Tooltip, 
    Typography, 
    Box, 
    Paper, 
    Divider, 
    IconButton,
    List,
    Collapse,
    useMediaQuery,
    useTheme,
    Button,
    CircularProgress,
    Avatar,
    ListItem,
    ListItemAvatar,
    ListItemText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CircleIcon from '@mui/icons-material/Circle';
import PauseIcon from '@mui/icons-material/Pause';
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { toZonedTime, fromZonedTime, format as formatTz } from 'date-fns-tz';
import { Link } from 'react-router-dom';
import OpportunityMatchListItem from './OpportunityMatchListItem';
import { getOpportunityInvitations, withdrawInvitation } from '../api/api';

const OpportunityListItem = ({
    opportunity,     
    placeIdToName,
    onEdit,
    onDelete, 
    onChangeActivate, 
    badgeColor,
    hint,
    token,
}) => {
    const [showInterest, setShowInterest] = useState(true);
    const [showInvitations, setShowInvitations] = useState(false);
    const [invitations, setInvitations] = useState([]);
    const [loadingInvitations, setLoadingInvitations] = useState(false);
    const [withdrawingId, setWithdrawingId] = useState(null);
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const timeZone = opportunity.tz ?? "America/Toronto";
    const utcDate = opportunity.dateTime;
    const zonedDate = toZonedTime(utcDate, timeZone);


    // Control buttons component
    const ControlButtons = () => (
        <Box sx={{ 
            display: 'flex', 
            gap: 1,
            flexDirection: isMobile ? 'column' : 'row'
        }}>
            <Tooltip title={t('opportunity.edit')}>
                <IconButton onClick={() => onEdit(opportunity)}>
                    <EditIcon />
                </IconButton>
            </Tooltip>
            
            {opportunity.isActive && onChangeActivate &&
                <Tooltip title={t('opportunity.pauseSearch')}>
                    <IconButton onClick={() => onChangeActivate(opportunity, false)}>
                        <PauseIcon />
                    </IconButton>
                </Tooltip>
            }
            
            {!opportunity.isActive && onChangeActivate &&
                <Tooltip title={t('opportunity.resumeSearch')}>
                    <IconButton onClick={() => onChangeActivate(opportunity, true)}>
                        <PlayCircleOutlineOutlinedIcon />
                    </IconButton>
                </Tooltip>
            }
            
            <Tooltip title={t('opportunity.delete')}>
                <IconButton 
                    onClick={() => onDelete(opportunity)}
                    color="error">
                    <DeleteIcon />
                </IconButton>
            </Tooltip>
        </Box>
    );
    
    return (
        <Paper
            elevation={1}
            sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                marginBottom: '8px',
                overflow: 'hidden'
            }}
        >
            {/* Header section */}
            <Box sx={{ 
                padding: isMobile ? 1.5 : 2,
                backgroundColor: '#f9f9f9'
            }}>
                {/* Desktop layout - all in one row */}
                {!isMobile && (
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center'
                    }}>
                        {/* Part 1: Status indicator */}
                        <Box sx={{ mr: 2 }}>
                            <Tooltip title={hint}>
                                <CircleIcon style={{ 
                                    color: badgeColor,
                                    fontSize: '40px'
                                }}/>
                            </Tooltip>
                        </Box>
                        
                        {/* Part 2: Opportunity name */}
                        <Box sx={{ flexGrow: 1 }}>
                            <Box>
                                <Typography 
                                    variant="h5" 
                                    component="div"
                                    sx={{ fontSize: '1.5rem' }}
                                >
                                    {opportunity.name 
                                        ? (opportunity.name.length > 50 
                                            ? `${opportunity.name.substring(0, 50)}...` 
                                            : opportunity.name) 
                                        : t('opportunity.form.name')}
                                </Typography>
                                <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{ fontSize: '0.875rem' }}
                                >
                                    {new Date(opportunity.dateTime) <= new Date() ? 
                                        t('opportunity.expiredHint').split('.')[0] 
                                    : opportunity.isActive 
                                        ? t('opportunity.activeHint').split('.')[0] + ". " + opportunity.notifiedCrew + " " + t('opportunity.crewNotified')+"."
                                        : t('opportunity.suspendedHint').split('.')[0]}
                                </Typography>
                            </Box>
                        </Box>
                        
                        {/* Part 3: Control buttons */}
                        <Box>
                            <ControlButtons />
                        </Box>
                    </Box>
                )}
                
                {/* Mobile layout - buttons on second row */}
                {isMobile && (
                    <>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            mb: 1.5
                        }}>
                            {/* Part 1: Status indicator */}
                            <Box sx={{ mr: 1.5 }}>
                                <Tooltip title={hint}>
                                    <CircleIcon style={{ 
                                        color: badgeColor,
                                        fontSize: '32px'
                                    }}/>
                                </Tooltip>
                            </Box>
                            
                            {/* Part 2: Opportunity name */}
                            <Box sx={{ flexGrow: 1 }}>
                                <Box>
                                    <Typography 
                                        variant="h6" 
                                        component="div"
                                        sx={{ fontSize: '1.1rem' }}
                                    >
                                        {opportunity.name 
                                            ? (opportunity.name.length > 50 
                                                ? `${opportunity.name.substring(0, 50)}...` 
                                                : opportunity.name) 
                                            : t('opportunity.form.name')}
                                    </Typography>
                                    <Typography 
                                        variant="body2" 
                                        color="text.secondary"
                                        sx={{ fontSize: '0.9rem' }}
                                    >
                                      {new Date(opportunity.dateTime) <= new Date() ? 
                                        t('opportunity.expiredHint').split('.')[0] 
                                        : opportunity.isActive 
                                        ? t('opportunity.activeHint').split('.')[0] + ". " + opportunity.notifiedCrew + " " + t('opportunity.crewNotified')+"."
                                        : t('opportunity.suspendedHint').split('.')[0]}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        
                        {/* Part 3: Control buttons on second row */}
                        <Box sx={{ 
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            <Box sx={{ 
                                display: 'flex',
                                gap: 1
                            }}>
                                <Tooltip title={t('opportunity.edit')}>
                                    <IconButton onClick={() => onEdit(opportunity)}>
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                                
                                {opportunity.isActive && onChangeActivate &&
                                    <Tooltip title={t('opportunity.pauseSearch')}>
                                        <IconButton onClick={() => onChangeActivate(opportunity, false)}>
                                            <PauseIcon />
                                        </IconButton>
                                    </Tooltip>
                                }
                                
                                {!opportunity.isActive && onChangeActivate &&
                                    <Tooltip title={t('opportunity.resumeSearch')}>
                                        <IconButton onClick={() => onChangeActivate(opportunity, true)}>
                                            <PlayCircleOutlineOutlinedIcon />
                                        </IconButton>
                                    </Tooltip>
                                }
                                
                                <Tooltip title={t('opportunity.delete')}>
                                    <IconButton 
                                        onClick={() => onDelete(opportunity)}
                                        color="error">
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </>
                )}
            </Box>
            
            <Divider />
            
            {/* Content section */}
            <Box sx={{ p: isMobile ? 1.5 : 2 }}>
                {/* Date and time */}
                <Stack 
                    direction="row" 
                    spacing={isMobile ? 1 : 2} 
                    sx={{ mb: 1 }}
                >
                    <Typography 
                        variant="subtitle1"
                        sx={{ fontSize: isMobile ? '1rem' : '1rem' }}
                    >
                        {formatTz(zonedDate, "PPP", { timeZone: timeZone })}
                        {/* //{formatTz(zonedDate, 'PPP')} */}
                    </Typography>
                    <Typography 
                        variant="subtitle1"
                        sx={{ fontSize: isMobile ? '1rem' : '1rem' }}
                    >
                        {t('opportunity.at')} { formatTz(zonedDate, 'p', { timeZone: timeZone }) }
                    </Typography>
                </Stack>
                
                {/* Location */}
                <Typography 
                    component="div" 
                    variant="body2" 
                    color="textPrimary" 
                    sx={{ 
                        mb: 0.5,
                        fontSize: isMobile ? '0.9rem' : '0.9rem'
                    }}
                >
                    {opportunity.type.charAt(0).toUpperCase() + opportunity.type.slice(1)} - {placeIdToName(opportunity.place)}
                </Typography>
                
                {/* Opportunity type */}
                <Typography 
                    component="div" 
                    variant="body2" 
                    sx={{ 
                        mb: 1,
                        fontSize: isMobile ? '0.875rem' : '0.875rem'
                    }}
                >
                   
                </Typography>
                
                {/* Note */}
                {opportunity.note && (
                    <Box sx={{ mt: 1, mb: 2 }}>
                        <Typography 
                            component="div" 
                            variant="body2" 
                            color="textSecondary"
                            sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                        >
                            {opportunity.note.length > 100 
                                ? `${opportunity.note.substring(0, 100)}...` 
                                : opportunity.note}
                        </Typography>
                    </Box>
                )}
                
                {/* Crew interest section */}
                <Box sx={{ mt: 2 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ 
                            cursor: 'pointer',
                            mb: 1
                        }}
                        onClick={() => setShowInterest(!showInterest)}
                    >
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                fontWeight: 'medium',
                                fontSize: isMobile ? '0.9rem' : '1rem'
                            }}
                        >
                            {t('opportunity.crewInterest')} ({opportunity.matches.length})
                        </Typography>
                        <IconButton size="small">
                            {showInterest ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Stack>
                    
                    <Collapse in={showInterest}>
                        {opportunity.matches.length > 0 ? (
                            <List sx={{ 
                                width: '100%',
                                bgcolor: '#f9f9f9',
                                borderRadius: '4px'
                            }}>
                                {opportunity.matches.map((match) => (
                                    <OpportunityMatchListItem key={match.user._id} match={match} />
                                ))}
                            </List>
                        ) : (
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    color: 'text.secondary', 
                                    pl: 2, 
                                    pb: 1,
                                    fontSize: isMobile ? '0.9rem' : '1rem'
                                }}
                            >
                                {t('opportunity.noInterest')}
                            </Typography>
                        )}
                    </Collapse>
                </Box>

                {/* Invitations section */}
                {opportunity.invitations && opportunity.invitations.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 2 }} />
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ 
                                cursor: 'pointer',
                                mb: 1
                            }}
                            onClick={() => setShowInvitations(!showInvitations)}
                        >
                            <MailOutlineIcon sx={{ fontSize: isMobile ? '1rem' : '1.2rem', color: 'primary.main' }} />
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    fontWeight: 'medium',
                                    fontSize: isMobile ? '0.9rem' : '1rem'
                                }}
                            >
                                {t('opportunity.invitations')} ({opportunity.invitations.length})
                            </Typography>
                            <IconButton size="small">
                                {showInvitations ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Stack>
                        
                        <Collapse in={showInvitations}>
                            <List sx={{ 
                                width: '100%',
                                bgcolor: '#f5f5ff',
                                borderRadius: '4px'
                            }}>
                                {opportunity.invitations.map((invitation) => (
                                    <ListItem 
                                        key={invitation._id}
                                        secondaryAction={
                                            <Tooltip title={t('opportunity.withdrawInvitation')}>
                                                <IconButton 
                                                    edge="end" 
                                                    color="error"
                                                    disabled={withdrawingId === invitation._id}
                                                    onClick={async () => {
                                                        if (token) {
                                                            setWithdrawingId(invitation._id);
                                                            try {
                                                                await withdrawInvitation(token, invitation._id);
                                                                // Remove from local state
                                                                setInvitations(prev => prev.filter(i => i._id !== invitation._id));
                                                            } catch (error) {
                                                                console.error('Error withdrawing invitation:', error);
                                                            } finally {
                                                                setWithdrawingId(null);
                                                            }
                                                        }
                                                    }}
                                                >
                                                    {withdrawingId === invitation._id ? (
                                                        <CircularProgress size={20} />
                                                    ) : (
                                                        <CancelIcon />
                                                    )}
                                                </IconButton>
                                            </Tooltip>
                                        }
                                    >
                                        <ListItemAvatar>
                                            <Link to={`/profile/${invitation.crew?.userId}`} style={{ textDecoration: 'none' }}>
                                                <Avatar 
                                                    alt={`${invitation.crew?.firstName} ${invitation.crew?.lastName}`}
                                                    src={invitation.crew?.hasPicture ? invitation.crew?.profilePictureUrl : null}
                                                />
                                            </Link>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Link 
                                                    to={`/profile/${invitation.crew?.userId}`} 
                                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                                >
                                                    {invitation.crew?.firstName} {invitation.crew?.lastName?.charAt(0)}.
                                                </Link>
                                            }
                                            secondary={t('opportunity.invitationPending')}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Collapse>
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

export default OpportunityListItem;
